<template>
  <overlay-template
    :title="'Average client value'"
    :primary-number="rate.goal.incomingPerClient"
    :primary-number-title="'Minimum target'"
    :secondary-number="rate.goal.outgoingPerClient"
    :secondary-number-title="'Expected average client break-even'"
  >
    <div slot="intro">
      <p>Revenue per client tells you how much revenue each client should generate your company to meet your annual revenue goals combined with your ideal number of clients.</p>
      <p>Ideally, you should try to work with larger, healthier sized clients to offset the capacity crunch that completing smaller value, shorter turnaround projects creates. Remember: all projects require a minimum amount of work to set up and run. Profitable projects are ones that bring in more revenue than you have to expend to run them (so larger ones).</p>
      <p>Blair Enns of <a
        href="https://winwithoutpitching.com"
        target="_blank">Win Without Pitching</a> calls this your <a
          href="https://www.winwithoutpitching.com/price-objections/"
          target="_blank">minimum level of engagement (MLOE)</a>, or the minimum amount of revenue your clients should agree to spend on your services over the year when they partner with you. He also recommends a base of between 10-15 active clients at one time.</p>
    </div>
    <p slot="formula">{{ rate.goal.incoming | currency }} total annual revenue &divide; {{ rate.goal.clients }} active clients</p>
    <p slot="break-even-formula">{{ rate.goal.outgoing | currency }} total annual break-even &divide; {{ rate.goal.clients }} active clients</p>
    <div
      slot="charts"
      class="grid-column-span-3_sm grid p0 mt3 grid-columns-2 grid-columns-4_md">
      <h2 class="grid-column-full">In vs. out</h2>
      <div class="mr1">
        <h5 class="semi-condensed">Average client value</h5>
        <lw-bar-chart
          :chart-data="clientSizeGraph"
          :format="'currency'" />
      </div>
      <div class="mr1">
        <h5 class="semi-condensed">Client size trend</h5>
        <lw-line-chart :chart-data="clientGrowthGraph" />
      </div>
      <div class="legend mb3">
        <h5 class="mb1">Average client value</h5>
        <div class="profit">Current<span class="block font-size-4 bold price">{{ rate.current.incomingPerClient | currency }}</span></div>
        <div class="profit">Goal <span class="block font-size-4 bold price">{{ rate.goal.incomingPerClient | currency }}</span></div>
      </div>
      <div class="legend mb3">
        <h5 class="mb1">Average client break-even</h5>
        <div class="expenses">Current <span class="block font-size-4 bold price">{{ rate.current.outgoingPerClient | currency }}</span></div>
        <div class="expenses">Goal <span class="block font-size-4 bold price">{{ rate.goal.outgoingPerClient | currency }}</span></div>
      </div>
      <div class="grid-column-full"><hr class="grey-border border-width-1 solid-border mb3"></div>
      <h2 class="grid-column-full mb0">Ideal billings per client per year</h2>
      <div class="grid-column-span-2 grid-column-span-1_lg mb4_md pr2">
        <div class="measure">
          <p>Clients that are too small still require a lot of care and love to support (often the same or more as larger clients). Clients that are too large can destroy your org if they leave without warning. Aim to maintain a healthy balanced client breakfast of different sizes. Remember: all projects require a minimal amount of effort to setup and run. Make sure the clients you partner with are worth the effort.</p>
          <p>There are a number of ways to calculate your ideal spread. We&rsquo;ve generated yours by spreading an even distribution across 40% of your target revenue.</p>
          <p>Read more about client composition from <a
            href="https://www.davidcbaker.com/3-guidelines-for-composition-of-your-client-base"
            target="_blank">David Baker</a> and <a
              href="https://www.winwithoutpitching.com/show-us-your-money/"
              target="_blank">Blair Enns</a>.</p>
        </div>
      </div>
      <div class="grid-column-span-2 grid-column-span-3_lg grid px0 grid-columns-1 grid-columns-5_lg mb4">
        <p class="font-size-2 mb2 dark-red-300-text"><span class="block font-size-7 extra-condensed medium">&lt;&nbsp;{{ low | currency }}</span> <span class="block semibold">{{ low / rate.goal.incoming * 100 | round1 }}% of total revenue</span> Unprofitable</p>
        <p class="font-size-2 mb2 yellow-text"><span class="block font-size-7 extra-condensed medium">{{ low | currency }} &ndash; {{ rate.goal.incomingPerClient | currency }}</span> <span class="block semibold">{{ low / rate.goal.incoming * 100 | round1 }} &ndash; {{ rate.goal.incomingPerClient / rate.goal.incoming * 100 | round1 }}% of total revenue</span> Try to increase</p>
        <p class="font-size-2 mb2 green-text"><span class="block font-size-7 extra-condensed medium">{{ rate.goal.incomingPerClient | currency }} &ndash; {{ mid | currency }}</span> <span class="block semibold">{{ rate.goal.incomingPerClient / rate.goal.incoming * 100 | round1 }} &ndash; {{ mid / rate.goal.incoming * 100 | round1 }}% of total revenue</span> Sweet spot</p>
        <p class="font-size-2 mb2 yellow-text"><span class="block font-size-7 extra-condensed medium">{{ mid | currency }} &ndash; {{ rate.goal.incoming * 0.4 | currency }}</span> <span class="block semibold">{{ mid / rate.goal.incoming * 100 | round1 }} &ndash; {{ rate.goal.incoming * 0.4 / rate.goal.incoming * 100 | round1 }}%</span> Warning</p>
        <p class="font-size-2 mb2 dark-red-300-text"><span class="block font-size-7 extra-condensed medium">> {{ rate.goal.incoming * 0.4 | currency }}</span> <span class="block semibold">+{{ rate.goal.incoming * 0.4 / rate.goal.incoming * 100 | round1 }}% of total revenue</span> High risk</p>
      </div>
    </div>
  </overlay-template>
</template>

<script>
import OverlayTemplate from './OverlayTemplate.vue'
import BarChart from '../../livewires/BarChart.vue'
import LineChart from '../../livewires/LineChart.vue'

export default {
  components: {
    overlayTemplate: OverlayTemplate,
    lwBarChart: BarChart,
    lwLineChart: LineChart
  },
  data () {
    return {
      page: this.$route.params.id
    }
  },
  computed: {
    rate () {
      return this.$store.getters.rateCard(this.page)
    },
    low () {
      const low = this.rate.goal.incomingPerClient / 2
      return low
    },
    mid () {
      return (((this.rate.goal.incoming * 0.4) - (this.low)) / 2) + this.rate.goal.incomingPerClient
    },
    clientSizeGraph () {
      return this.$store.getters.clientSizeGraph(this.page)
    },
    clientGrowthGraph () {
      return this.$store.getters.clientGrowthGraph(this.page)
    }
  }
}
</script>
